import { ColumnDef } from "@tanstack/react-table";
import { get, omit, pick } from "lodash-es";

import { DisplayDateTime } from "src/base-components/Date";
import { TagPill } from "src/base-components/TagPill";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { CasePropertySchema } from "src/cases/queues/types";
import { getPropertyDisplay, getSubValueDisplayName } from "src/cases/utils";
import {
  CaseTemplateVersionAttributes,
  UpdatedPropertiesValue,
} from "src/clients/case-management";
import { formatNumber } from "src/utils/numbers";
import { unslugify } from "src/utils/pythonUtils";

type TableRow = {
  propertyKey: string;
  propertyValue: unknown;
  propertyConfig: CasePropertySchema;
};

export const columns: ColumnDef<TableRow, unknown>[] = [
  {
    accessorKey: "propertyKey",
    header: "Property",
    cell: (info) => {
      const schema = info.row.original.propertyConfig;

      return schema?.display_name ?? unslugify(info.row.original.propertyKey);
    },
  },
  {
    accessorKey: "propertyValue",
    header: "After",
    cell: (info) => {
      const schema = info.row.original.propertyConfig;
      const value = info.getValue();

      return (
        <CasePropertyValue
          propertyKey={info.row.original.propertyKey}
          schema={schema}
          value={value}
        />
      );
    },
  },
];

export const useCasePropertiesUpdatedTable = (
  updated_properties: UpdatedPropertiesValue,
  templateVersion:
    | ({
        id: string;
        type: "case_template_versions";
      } & CaseTemplateVersionAttributes)
    | undefined,
) => {
  if (!templateVersion) {
    return {
      columns,
      rows: [],
    };
  }

  const status = pick(updated_properties, ["status", "sub_status"]);
  const hasStatus = Object.keys(status).length > 0;
  const priority = get(updated_properties, "priority", undefined);
  const updatedProperties = omit(updated_properties, [
    "status",
    "sub_status",
    "priority",
  ]);

  const rows: TableRow[] = [
    hasStatus && {
      propertyKey: "status",
      propertyValue: status,
      propertyConfig: templateVersion.case_properties.status,
    },
    priority && {
      propertyKey: "priority",
      propertyValue: priority,
      propertyConfig: templateVersion.case_properties.priority,
    },
    ...Object.entries(updatedProperties).map(
      ([propertyKey, propertyValue]) => ({
        propertyKey,
        propertyValue,
        propertyConfig: templateVersion.case_properties[propertyKey],
      }),
    ),
  ].filter(Boolean) as TableRow[];

  return {
    columns,
    rows,
  };
};

type StatusValue = {
  status: string;
  sub_status: string;
};

export const CasePropertyValue: React.FC<{
  propertyKey: string;
  value: unknown;
  schema?: CasePropertySchema;
}> = ({ propertyKey, value, schema }) => {
  if (!schema) {
    return typeof value === "string" ? value : String(value);
  }

  if (schema.type === "enum" && propertyKey === "status") {
    const statusValue = value as StatusValue;
    const { variant, displayName: statusDisplayName } = getPropertyDisplay(
      statusValue.status,
      schema,
    );
    const subStatusDisplayName = getSubValueDisplayName(
      statusValue.status,
      statusValue.sub_status,
      schema,
    );

    return (
      <CaseStatusPill
        status={statusDisplayName}
        subStatus={subStatusDisplayName || statusValue.sub_status}
        variant={variant}
      />
    );
  }

  if (schema.type === "enum") {
    const enumVal = schema.values?.find((v) => v.value === value);
    return <TagPill value={enumVal || String(value)} />;
  }

  if (schema.type === "datetime") {
    const datetimeValue = value as string;
    return <DisplayDateTime value={datetimeValue} />;
  }

  if (schema.type === "tags") {
    const tagsValue = value as string[];
    return (
      <div className="flex flex-wrap gap-1">
        {tagsValue.map((tag) => {
          const tagValue = schema.values?.find((v) => v.value === tag);
          return <TagPill key={tag} value={tagValue || tag} />;
        })}
      </div>
    );
  }

  if (
    (schema.type === "entity_ref" ||
      schema.type === "event_ref" ||
      schema.type === "string") &&
    typeof value === "string"
  ) {
    return value;
  }

  if (schema.type === "integer" || schema.type === "number") {
    return typeof value === "number" ? formatNumber(value) : String(value);
  }

  return null;
};
